import React, { useEffect, useRef, useState } from "react";
import { Check, ImagePlus, Loader2, Trash2 } from "lucide-react";

import {
  createExistingBrandSource,
  deleteExistingBrandSource,
  listExistingBrandSources,
  updateExistingBrandSource,
  uploadUserMediaFile,
} from "@/lib/brandStudioApi";

const TYPES = [
  ["logo", "Логотип"],
  ["brandbook", "Брендбук"],
  ["font", "Шрифт"],
  ["color", "Цвет"],
  ["packaging", "Упаковка"],
  ["photo", "Фотография"],
  ["social", "Соцсети"],
  ["print", "Печатный материал"],
  ["other", "Другое"],
];

const INTENTS = [
  ["preserve", "Сохранить"],
  ["review", "Посмотреть"],
  ["improve", "Улучшить"],
];

export default function ExistingBrandStartV2({ onStoryNeeded, hasStory = false }) {
  const fileRef = useRef(null);
  const [sources, setSources] = useState([]);
  const [status, setStatus] = useState("loading");
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");

  const load = async () => {
    setStatus("loading");
    setError("");
    try {
      setSources(await listExistingBrandSources());
      setStatus("ready");
    } catch {
      setStatus("error");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const upload = async (file) => {
    if (!file || busy) return;
    setBusy("upload");
    setError("");
    try {
      const media = await uploadUserMediaFile(file, {
        title: file.name.replace(/\.[^.]+$/, ""),
      });
      const created = await createExistingBrandSource({
        media_id: media.id,
        asset_type: "other",
        intent: "review",
        note: "",
      });
      setSources((previous) => [...previous, created]);
    } catch {
      setError("Не удалось добавить материал. Попробуй ещё раз.");
    } finally {
      setBusy("");
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const patch = async (id, payload) => {
    setBusy(id);
    setError("");
    try {
      const updated = await updateExistingBrandSource(id, payload);
      setSources((previous) =>
        previous.map((item) => (item.id === id ? updated : item)),
      );
    } catch {
      setError("Не удалось сохранить изменение.");
    } finally {
      setBusy("");
    }
  };

  const remove = async (id) => {
    setBusy(id);
    setError("");
    try {
      await deleteExistingBrandSource(id);
      setSources((previous) => previous.filter((item) => item.id !== id));
    } catch {
      setError("Не удалось убрать материал.");
    } finally {
      setBusy("");
    }
  };

  return (
    <section
      className="rounded-[22px] border border-[#E1D4C2] bg-[#FFFDF9] p-5 md:p-7"
      data-testid="existing-brand-start-v2"
    >
      <div className="text-[9.5px] uppercase tracking-[0.22em] text-[#8A6D53]">
        У МЕНЯ УЖЕ ЕСТЬ БРЕНД
      </div>
      <h2 className="mt-3 font-serif text-[31px] leading-none text-[#2B211A] md:text-[36px]">
        Покажи, что у тебя уже есть
      </h2>
      <p className="mt-3 max-w-2xl text-[13.5px] leading-6 text-[#655141]">
        Добавь логотипы, упаковку, фотографии, страницы брендбука, примеры соцсетей
        и другие визуальные материалы. Для каждого можно отметить, что важно сохранить,
        а что хочется пересмотреть.
      </p>

      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
        className="hidden"
        onChange={(event) => event.target.files?.[0] && upload(event.target.files[0])}
        data-testid="existing-brand-file-input"
      />
      <button
        type="button"
        disabled={Boolean(busy)}
        onClick={() => fileRef.current?.click()}
        className="mt-5 inline-flex min-h-12 items-center gap-2 rounded-full bg-[#3D2A20] px-6 text-sm text-[#FFF8ED] disabled:opacity-50"
        data-testid="existing-brand-upload"
      >
        {busy === "upload" ? (
          <Loader2 size={16} className="animate-spin" />
        ) : (
          <ImagePlus size={16} />
        )}
        Добавить материал
      </button>
      <p className="mt-2 text-[11.5px] text-[#9A8871]">
        JPG, PNG или WEBP.
      </p>

      {status === "loading" && (
        <div className="mt-5 inline-flex items-center gap-2 text-sm text-[#796652]">
          <Loader2 size={16} className="animate-spin" /> Открываю материалы…
        </div>
      )}

      {status === "error" && (
        <button
          type="button"
          onClick={load}
          className="mt-5 min-h-11 rounded-full border border-[#D2C1AA] bg-white px-5 text-sm text-[#4A3728]"
        >
          Попробовать снова
        </button>
      )}

      {status === "ready" && sources.length > 0 && (
        <div className="mt-6 grid gap-4 md:grid-cols-2">
          {sources.map((item) => (
            <article
              key={item.id}
              className="overflow-hidden rounded-[14px] border border-[#E4D7C6] bg-[#FAF6EF]"
            >
              <div className="aspect-[4/3] overflow-hidden border-b border-[#E4D7C6] bg-white">
                <img
                  src={item.file_url}
                  alt={item.title || "Материал бренда"}
                  className="h-full w-full object-contain"
                />
              </div>
              <div className="p-4">
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="text-[11px] uppercase tracking-[0.12em] text-[#927A5B]">
                    Что это
                    <select
                      value={item.asset_type}
                      disabled={busy === item.id}
                      onChange={(event) =>
                        patch(item.id, { asset_type: event.target.value })
                      }
                      className="mt-1.5 min-h-11 w-full rounded-xl border border-[#D8C7AE] bg-white px-3 text-[13px] normal-case tracking-normal text-[#3E2E20]"
                    >
                      {TYPES.map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="text-[11px] uppercase tracking-[0.12em] text-[#927A5B]">
                    Что с этим делать
                    <select
                      value={item.intent}
                      disabled={busy === item.id}
                      onChange={(event) =>
                        patch(item.id, { intent: event.target.value })
                      }
                      className="mt-1.5 min-h-11 w-full rounded-xl border border-[#D8C7AE] bg-white px-3 text-[13px] normal-case tracking-normal text-[#3E2E20]"
                    >
                      {INTENTS.map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>

                <label className="mt-3 block text-[11px] uppercase tracking-[0.12em] text-[#927A5B]">
                  Комментарий
                  <textarea
                    defaultValue={item.note}
                    disabled={busy === item.id}
                    onBlur={(event) => {
                      if (event.target.value !== item.note) {
                        patch(item.id, { note: event.target.value });
                      }
                    }}
                    rows={3}
                    placeholder="Например: нравится только знак и цвет; шрифт хочу поменять."
                    className="mt-1.5 w-full resize-y rounded-xl border border-[#D8C7AE] bg-white p-3 text-[13px] leading-5 normal-case tracking-normal text-[#3E2E20]"
                  />
                </label>

                <div className="mt-3 flex items-center justify-between gap-3">
                  <div className="inline-flex items-center gap-1.5 text-[11.5px] text-[#806B55]">
                    <Check size={13} /> Материал сохранён
                  </div>
                  <button
                    type="button"
                    disabled={busy === item.id}
                    onClick={() => remove(item.id)}
                    className="inline-flex min-h-10 items-center gap-1.5 text-[12px] text-[#7E5145]"
                  >
                    <Trash2 size={14} /> Убрать
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      {status === "ready" && sources.length === 0 && (
        <div className="mt-6 rounded-[14px] border border-dashed border-[#D8C7AE] bg-[#FAF6EF] p-5 text-[13px] leading-5 text-[#806B55]">
          Здесь появятся материалы, которые ты добавишь.
        </div>
      )}

      <div className="mt-6 border-t border-[#EEE3D5] pt-5">
        <h3 className="font-serif text-[25px] text-[#2B211A]">
          {hasStory ? "Рассказ о бренде уже сохранён" : "И ещё расскажи контекст"}
        </h3>
        <p className="mt-2 max-w-2xl text-[13px] leading-5 text-[#806B55]">
          {hasStory
            ? "Его можно открыть, перечитать или создать новую подтверждённую версию."
            : "Что в бренде тебе нравится сейчас? Что обязательно сохранить? Что хочется изменить? Этот рассказ поможет не переделывать то, что уже работает."}
        </p>
        <button
          type="button"
          onClick={onStoryNeeded}
          className="mt-4 min-h-11 rounded-full border border-[#D2C1AA] bg-white px-5 text-[13px] text-[#4A3728]"
        >
          {hasStory ? "Посмотреть рассказ" : "Добавить рассказ о бренде"}
        </button>
      </div>

      {error && (
        <div className="mt-4 rounded-xl border border-[#E2CFC8] bg-[#FFF8F4] p-4 text-[13px] text-[#6B443A]">
          {error}
        </div>
      )}
    </section>
  );
}
